import { useMemo } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import rehypeSanitize from "rehype-sanitize";

interface MarkdownRendererProps {
  content: string;
}

export function MarkdownRenderer({ content }: MarkdownRendererProps) {
  const rendered = useMemo(() => (
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      rehypePlugins={[rehypeRaw, rehypeSanitize]}
      components={{
        h2: ({ children, ...props }) => (
          <h2 className="font-heading text-2xl text-navy mt-12 mb-4 leading-snug" {...props}>
            {children}
          </h2>
        ),
        h3: ({ children, ...props }) => (
          <h3 className="font-heading text-xl text-navy mt-9 mb-3 leading-snug" {...props}>
            {children}
          </h3>
        ),
        h4: ({ children, ...props }) => (
          <h4 className="font-bold text-lg text-navy mt-6 mb-2" {...props}>
            {children}
          </h4>
        ),
        p: ({ children, ...props }) => (
          <p className="text-[17px] leading-[1.7] text-[#1a1a1a] mb-5" {...props}>
            {children}
          </p>
        ),
        a: ({ children, href, ...props }) => {
          const isExternal = href?.startsWith("http");
          return (
            <a
              href={href}
              className="text-coral underline decoration-coral/30 hover:decoration-coral transition-colors"
              {...(isExternal ? { target: "_blank", rel: "noopener noreferrer" } : {})}
              {...props}
            >
              {children}
            </a>
          );
        },
        blockquote: ({ children, ...props }) => (
          <blockquote
            className="border-l-[3px] border-butter pl-5 my-6 italic text-charcoal/70 bg-cream/50 py-3 pr-4 rounded-r-lg"
            {...props}
          >
            {children}
          </blockquote>
        ),
        ul: ({ children, ...props }) => (
          <ul className="list-disc pl-6 mb-5 space-y-2 text-[17px] leading-[1.7] text-[#1a1a1a]" {...props}>
            {children}
          </ul>
        ),
        ol: ({ children, ...props }) => (
          <ol className="list-decimal pl-6 mb-5 space-y-2 text-[17px] leading-[1.7] text-[#1a1a1a]" {...props}>
            {children}
          </ol>
        ),
        li: ({ children, ...props }) => (
          <li className="pl-1" {...props}>{children}</li>
        ),
        img: ({ src, alt, ...props }) => (
          <figure className="my-8">
            <img
              src={src}
              alt={alt || ""}
              loading="lazy"
              decoding="async"
              className="w-full rounded-lg"
              {...props}
            />
            {alt && <figcaption className="text-center text-sm text-charcoal/50 mt-2">{alt}</figcaption>}
          </figure>
        ),
        code: ({ children, className, ...props }) => {
          const isBlock = className?.includes("language-");
          if (isBlock) {
            return (
              <code className={`block ${className}`} {...props}>
                {children}
              </code>
            );
          }
          return (
            <code className="bg-charcoal/[0.06] px-1.5 py-0.5 rounded text-[0.9em] text-navy font-mono" {...props}>
              {children}
            </code>
          );
        },
        pre: ({ children, ...props }) => (
          <pre className="bg-[#f5f5f5] rounded-lg p-4 overflow-x-auto mb-5 text-sm font-mono" {...props}>
            {children}
          </pre>
        ),
        hr: () => <hr className="my-10 border-charcoal/10" />,
        table: ({ children, ...props }) => (
          <div className="overflow-x-auto mb-5">
            <table className="w-full text-sm border-collapse" {...props}>
              {children}
            </table>
          </div>
        ),
        th: ({ children, ...props }) => (
          <th className="border border-charcoal/15 px-3 py-2 bg-cream/50 text-left font-bold text-navy" {...props}>
            {children}
          </th>
        ),
        td: ({ children, ...props }) => (
          <td className="border border-charcoal/15 px-3 py-2 text-charcoal/80" {...props}>
            {children}
          </td>
        ),
      }}
    >
      {content}
    </ReactMarkdown>
  ), [content]);

  return <div className="blog-prose">{rendered}</div>;
}
